import { useState } from "react"
import { Search, Filter, Star, Download, ArrowRight, Grid, List, Zap, Shield, Globe, Palette } from "lucide-react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Card } from "../ui/card"
import { Badge } from "../ui/badge"

interface App {
  id: string
  name: string
  description: string
  category: string
  icon: string
  rating: number
  downloads: number
  price: number
  featured: boolean
  tags: string[]
}

export default function Marketplace() {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")

  const categories = [
    { id: "all", label: "All Apps" },
    { id: "productivity", label: "Productivity" },
    { id: "collaboration", label: "Collaboration" },
    { id: "analytics", label: "Analytics" },
    { id: "integration", label: "Integration" },
    { id: "security", label: "Security" },
  ]

  // Mock data - replace with actual API call
  const apps: App[] = [
    {
      id: "1",
      name: "Screen Sharing Pro",
      description: "Advanced screen sharing with annotation tools",
      category: "collaboration",
      icon: "🖥️",
      rating: 4.8,
      downloads: 12500,
      price: 0,
      featured: true,
      tags: ["screen-share", "annotation", "collaboration"],
    },
    {
      id: "2",
      name: "AI Meeting Notes",
      description: "Automated meeting transcription and summarization",
      category: "productivity",
      icon: "🤖",
      rating: 4.9,
      downloads: 8900,
      price: 9.99,
      featured: true,
      tags: ["ai", "transcription", "notes"],
    },
    {
      id: "3",
      name: "Poll Master",
      description: "Create interactive polls and surveys in meetings",
      category: "collaboration",
      icon: "📊",
      rating: 4.6,
      downloads: 5600,
      price: 0,
      featured: false,
      tags: ["polls", "surveys", "interactive"],
    },
    {
      id: "4",
      name: "Calendar Sync",
      description: "Sync meetings with Google Calendar and Outlook",
      category: "integration",
      icon: "📅",
      rating: 4.7,
      downloads: 7800,
      price: 0,
      featured: true,
      tags: ["calendar", "sync", "integration"],
    },
    {
      id: "5",
      name: "Meeting Analytics",
      description: "Detailed analytics and insights for meetings",
      category: "analytics",
      icon: "📈",
      rating: 4.5,
      downloads: 3200,
      price: 19.99,
      featured: false,
      tags: ["analytics", "insights", "reports"],
    },
    {
      id: "6",
      name: "Secure Meeting",
      description: "End-to-end encryption for sensitive meetings",
      category: "security",
      icon: "🔒",
      rating: 4.8,
      downloads: 4100,
      price: 14.99,
      featured: false,
      tags: ["security", "encryption", "privacy"],
    },
    {
      id: "7",
      name: "Whiteboard Plus",
      description: "Interactive whiteboard with real-time collaboration",
      category: "collaboration",
      icon: "🎨",
      rating: 4.7,
      downloads: 6700,
      price: 0,
      featured: false,
      tags: ["whiteboard", "collaboration", "drawing"],
    },
    {
      id: "8",
      name: "Background Blur",
      description: "AI-powered background blur and replacement",
      category: "productivity",
      icon: "📷",
      rating: 4.4,
      downloads: 9200,
      price: 0,
      featured: false,
      tags: ["background", "ai", "video"],
    },
  ]

  const filteredApps = apps.filter((app) => {
    const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    const matchesCategory = selectedCategory === "all" || app.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "productivity":
        return <Zap className="w-4 h-4" />
      case "collaboration":
        return <Grid className="w-4 h-4" />
      case "analytics":
        return <List className="w-4 h-4" />
      case "integration":
        return <Globe className="w-4 h-4" />
      case "security":
        return <Shield className="w-4 h-4" />
      default:
        return <Palette className="w-4 h-4" />
    }
  }

  return (
    <div className="bg-slate-50 dark:bg-card min-h-full p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white mb-2">
            Marketplace
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Discover and install apps to enhance your meeting experience
          </p>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search apps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="h-12">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <div className="flex border border-slate-300 dark:border-slate-600 rounded-lg overflow-hidden">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="icon"
                onClick={() => setViewMode("grid")}
                className="h-12 w-12 rounded-none"
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="icon"
                onClick={() => setViewMode("list")}
                className="h-12 w-12 rounded-none"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              onClick={() => setSelectedCategory(category.id)}
              className="h-10 whitespace-nowrap"
            >
              {getCategoryIcon(category.id)}
              <span className="ml-2">{category.label}</span>
            </Button>
          ))}
        </div>

        {/* Featured Apps */}
        {selectedCategory === "all" && searchQuery === "" && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-4">
              Featured Apps
            </h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {apps.filter(app => app.featured).slice(0, 3).map((app) => (
                <Card
                  key={app.id}
                  className="p-6 hover:shadow-lg transition-shadow cursor-pointer border-2 border-primary/20"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-4xl">{app.icon}</div>
                    <Badge className="bg-primary">Featured</Badge>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-2">
                    {app.name}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                    {app.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium text-slate-900 dark:text-foreground/80 dark:text-white">
                        {app.rating}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                      <Download className="w-4 h-4" />
                      <span>{app.downloads.toLocaleString()}</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* All Apps */}
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-4">
            {selectedCategory === "all" ? "All Apps" : categories.find(c => c.id === selectedCategory)?.label}
          </h2>
          
          {viewMode === "grid" ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredApps.map((app) => (
                <Card
                  key={app.id}
                  className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-4xl">{app.icon}</div>
                    {app.featured && <Badge className="bg-primary">Featured</Badge>}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-2">
                    {app.name}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                    {app.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {app.tags.slice(0, 2).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs capitalize">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium text-slate-900 dark:text-foreground/80 dark:text-white">
                        {app.rating}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                      <Download className="w-4 h-4" />
                      <span>{app.downloads.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-slate-900 dark:text-foreground/80 dark:text-white">
                      {app.price === 0 ? "Free" : `$${app.price}`}
                    </span>
                    <Button size="sm" className="h-8">
                      {app.price === 0 ? "Install" : "Buy"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredApps.map((app) => (
                <Card
                  key={app.id}
                  className="p-4 hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">{app.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">
                          {app.name}
                        </h3>
                        {app.featured && <Badge className="bg-primary text-xs">Featured</Badge>}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-1">
                        {app.description}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-600 dark:text-slate-400">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span>{app.rating}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Download className="w-4 h-4" />
                          <span>{app.downloads.toLocaleString()}</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {app.tags.slice(0, 2).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs capitalize">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-900 dark:text-foreground/80 dark:text-white mb-2">
                        {app.price === 0 ? "Free" : `$${app.price}`}
                      </p>
                      <Button size="sm" className="h-8">
                        {app.price === 0 ? "Install" : "Buy"}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Empty State */}
        {filteredApps.length === 0 && (
          <div className="text-center py-16">
            <Palette className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-2">
              No apps found
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              {searchQuery || selectedCategory !== "all"
                ? "Try adjusting your search or filters"
                : "Check back soon for new apps"}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
